/**
 * SalesActivityByType — R5 wave 2 (2026-05-17).
 *
 * Per PDF section 2.5 Sales Statistics box: "Sales activity type" report.
 * Events grouped by type with event count, total attendance, verified
 * members, and voucher count.
 *
 * Aggregation happens server-side in get_sales_activity_by_type RPC so
 * there's no N+1 per type.
 */

import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getSalesActivityByType,
  type AggregationFilter,
  type SalesActivityByTypeRow,
} from "../../services/reportsSuite.service";
import ExportButton from "../../components/common/ExportButton";
import ReportFiltersBar, {
  type ReportFilterState,
} from "../../components/reports/ReportFiltersBar";
import ReportPageHeader from "../../components/reports/ReportPageHeader";
import { EVENT_TYPES } from "../../utils/constants";

const PAGE_SIZE = 50;

const filterToRpc = (
  f: ReportFilterState,
  page: number,
  limit: number,
): AggregationFilter => ({
  orgIds: f.orgId ? [f.orgId] : undefined,
  state: f.state,
  dateFrom: f.dateFrom,
  dateTo: f.dateTo,
  page,
  limit,
});

// Map a raw type code to its display label using the shared EVENT_TYPES
// constant. Falls back to the raw value for legacy/deprecated types.
const typeLabel = (raw: string): string => {
  const found = EVENT_TYPES.find((t) => t.value === raw);
  return found ? found.label : raw;
};

// Local calendar date (YYYY-MM-DD) n days before today. Local, not UTC, so the
// "From" box shows the date the operator expects late in the evening.
const localDateDaysAgo = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const DEFAULT_WINDOW_DAYS = 90;

// The report opens on the last 90 days (2026-10-30): From = today - 89 (so the
// window is exactly 90 calendar days including today) and NO To date. An
// open-ended To matters: the RPC compares p_date_to in UTC, so a To of "today"
// would drop events later today in US time. "All time" clears both dates (fast
// since migration 1285).
const defaultFromDate = (): string => localDateDaysAgo(DEFAULT_WINDOW_DAYS - 1);

const defaultFilters = (): ReportFilterState => ({
  dateFrom: defaultFromDate(),
  dateTo: undefined,
});

const SalesActivityByType: React.FC = () => {
  const [filters, setFilters] = useState<ReportFilterState>(defaultFilters);
  const [page, setPage] = useState(1);

  React.useEffect(() => {
    setPage(1);
  }, [filters.dateFrom, filters.dateTo, filters.state, filters.orgId]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["report-sales-activity-by-type", filters, page],
    queryFn: () =>
      getSalesActivityByType(filterToRpc(filters, page, PAGE_SIZE)),
    keepPreviousData: true,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const EXPORT_COLUMNS = [
    "Event Type",
    "Event Count",
    "Total Attendance",
    "Total Verified Members",
    "Total Vouchers",
  ];

  const mapRows = (
    pageRows: SalesActivityByTypeRow[],
  ): Record<string, unknown>[] => {
    return pageRows.map((r) => ({
      "Event Type": typeLabel(r.event_type),
      "Event Count": r.event_count,
      "Total Attendance": r.total_attendance,
      "Total Verified Members": r.total_verified_members,
      "Total Vouchers": r.total_vouchers,
    }));
  };

  return (
    <div className="space-y-4">
      <ReportPageHeader
        title="Sales Activity by Type"
        description="Events grouped by type. Aggregations server-side; values reflect applied filters."
        actions={
          <ExportButton
            filters={filters}
            fetchPage={(f, p, l) =>
              getSalesActivityByType(filterToRpc(f, p, l)).then((res) => ({
                rows: res.rows,
                total: res.total,
              }))
            }
            mapRows={mapRows}
            columns={EXPORT_COLUMNS}
            filename="sales-activity-by-type"
          />
        }
      />

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-gray-600">Date range:</span>
        <button
          type="button"
          onClick={() => setFilters((f) => ({ ...f, ...defaultFilters() }))}
          className="rounded border border-gray-300 px-3 py-1 hover:bg-gray-50"
          data-testid="sales-by-type-last-90"
        >
          Last {DEFAULT_WINDOW_DAYS} days (from {defaultFromDate()})
        </button>
        <button
          type="button"
          onClick={() =>
            setFilters((f) => ({
              ...f,
              dateFrom: undefined,
              dateTo: undefined,
            }))
          }
          className="rounded border border-gray-300 px-3 py-1 hover:bg-gray-50"
          data-testid="sales-by-type-all-time"
        >
          All time
        </button>
        <span className="text-xs text-gray-500">
          {filters.dateFrom || filters.dateTo
            ? `Showing events from ${filters.dateFrom ?? "the beginning"} to ${filters.dateTo ?? "the latest"}.`
            : "Showing all events (all time)."}
        </span>
      </div>

      <ReportFiltersBar
        filters={filters}
        onChange={setFilters}
        showType={false}
        showStatus={false}
      />
      <p
        className="-mt-2 text-xs text-gray-500"
        data-testid="sales-by-type-utc-hint"
      >
        From / To dates are in UTC (an event late in the evening US time can
        fall on the next day). Leave To empty to include everything after From.
      </p>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2 text-right">Events</th>
              <th className="px-3 py-2 text-right">Attendance</th>
              <th className="px-3 py-2 text-right">Verified</th>
              <th className="px-3 py-2 text-right">Vouchers</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {error && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-red-600">
                  {(error as Error).message}
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                  No events match the current filters.
                </td>
              </tr>
            )}
            {rows.map((r: any) => (
              <tr key={r.event_type} className="hover:bg-gray-50">
                <td className="px-3 py-2">{typeLabel(r.event_type)}</td>
                <td className="px-3 py-2 text-right">
                  {r.event_count.toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right">
                  {r.total_attendance.toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right">
                  {r.total_verified_members.toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right">
                  {r.total_vouchers.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-600">
        <div>
          {total > 0
            ? `Page ${page} of ${totalPages} — ${total.toLocaleString()} types`
            : ""}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default SalesActivityByType;
